import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { InView } from 'react-intersection-observer';
import uuidv4 from 'uuid/v4';

import {
  catalogBibShape,
  libraryShape,
  listShape,
  paginationShape,
  userIdentityShape,
  routerShape
} from '@bibliocommons/bc-prop-types';
import { PaginationLabel } from '@bibliocommons/pagination';
import { bindAll } from '@bibliocommons/utils-react';
import { withRouter } from '@bibliocommons/utils-routing';

import CommunityDisclaimer from 'app/components/bibs/shared/ugc/CommunityDisclaimer';
import BibSubPage from 'app/components/bibs/subpages/BibSubPage';
import ListCardAdapter from 'app/components/shared/ListCardAdapter';
import Tabs from 'app/components/shared/Tabs';
import { LIST_CATEGORIES } from 'app/constants/ListsConstants';

import './AllListsPage.scss';

const { COMMUNITY, STAFF } = LIST_CATEGORIES;
const makeImpressionId = (listId, impressionIdSuffix) => `${listId}-${impressionIdSuffix}`;

export class AllListsPage extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  onTabSelected(tabOption) {
    const { id: category } = tabOption;

    this.props.router.push({ query: { category } });
  }

  renderItem = list => {
    const { library, users, handleListTitleClick, handleListImageClick, handleListImpression } = this.props;
    const creator = users.get(list.getIn(['owner', 'id']).toString());
    const impressionIdSuffix = uuidv4();
    const listId = list.get('id');
    const handleVisibility = visibleListId => {
      return isVisible => {
        if (isVisible) {
          handleListImpression({ impressionId: makeImpressionId(visibleListId, impressionIdSuffix), listId });
        }
      };
    };

    return (
      <li key={listId}>
        <InView threshold={1} triggerOnce onChange={handleVisibility(listId)}>
          <ListCardAdapter
            creator={creator}
            handleListImageClick={() =>
              handleListImageClick({
                impressionId: makeImpressionId(listId, impressionIdSuffix),
                listId
              })}
            handleListTitleClick={() =>
              handleListTitleClick({
                impressionId: makeImpressionId(listId, impressionIdSuffix),
                listId,
                listTitle: list.get('name')
              })}
            horizontal
            library={library}
            list={list}
            showUserBadge
            showDate
          />
        </InView>
      </li>
    );
  };

  renderLists = () => {
    const { category, library, lists, pagination } = this.props;

    const hasCommunityDisclaimer = category === COMMUNITY && library?.get('ugcDisclaimerMessageEnabled');

    return (
      <div>
        {hasCommunityDisclaimer && <CommunityDisclaimer currentLibrary={library} type="lists" />}
        {pagination.get('count') > 0 && (
          <div className="pagination">
            <PaginationLabel pagination={pagination} autoFocus />
          </div>
        )}
        {lists.size ? (
          <ul className="lists-items">{lists.map(this.renderItem)}</ul>
        ) : (
          <div className="no-content-placeholder">
            <FormattedMessage id={`empty_${category.toLowerCase()}_lists`} />
          </div>
        )}
      </div>
    );
  };

  renderTabs() {
    const { category, intl } = this.props;

    const tabOptions = [
      {
        id: STAFF,
        label: intl.formatMessage({ id: `${STAFF.toLowerCase()}_lists` }),
        renderPanelBody: this.renderLists
      },
      {
        id: COMMUNITY,
        label: intl.formatMessage({ id: `${COMMUNITY.toLowerCase()}_lists` }),
        renderPanelBody: this.renderLists
      }
    ];

    return (
      <div className="tabs">
        <Tabs
          ariaLabel={intl.formatMessage({ id: 'lists_featuring_title' })}
          initialSelectedOptionId={category}
          onTabSelected={this.onTabSelected}
          options={tabOptions}
        />
      </div>
    );
  }

  render() {
    const { catalogBib, pagination, isFetching } = this.props;

    return (
      <div className="cp-all-lists-page">
        <BibSubPage
          catalogBib={catalogBib}
          headerLocKey="lists_featuring_title"
          pagination={pagination}
          showHeaderCount={false}
          isFetching={isFetching}
        >
          {this.renderTabs()}
        </BibSubPage>
      </div>
    );
  }
}

AllListsPage.propTypes = {
  catalogBib: catalogBibShape.isRequired,
  category: PropTypes.oneOf([COMMUNITY, STAFF]).isRequired,
  library: libraryShape.isRequired,
  lists: ImmutablePropTypes.listOf(listShape).isRequired,
  users: ImmutablePropTypes.mapOf(userIdentityShape).isRequired,
  pagination: paginationShape.isRequired,
  router: routerShape.isRequired,
  isFetching: PropTypes.bool,
  handleListTitleClick: PropTypes.func,
  handleListImageClick: PropTypes.func,
  handleListImpression: PropTypes.func
};

export default withRouter(injectIntl(AllListsPage));
